import type { ChangeEvent } from 'react'
import { useCallback, useState } from 'react'

import type { MessageSequenceResponseDto } from '@api'
import { MessageSequenceStatus } from '@api'
import type { Row } from '@tanstack/react-table'
import { useNavigate } from 'react-router'

import EditIcon from '@/assets/icons/icon-create.svg'
import { IconLoading } from '@/assets/icons/icon-loading.tsx'
import IconMore from '@/assets/icons/icon-menu-more.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Toggle } from '@/components/ui/Toggle.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { ConfirmDeleteDialog } from '@/modules/shifts/ui/dialogs/confirm-delete.tsx'

interface ActionButtonProps<T> {
  row: Row<T>
  deleteMessage: (messageId: number) => Promise<unknown>
  toggleMessage: (messageId: number, value: boolean) => Promise<unknown>
}

export const SequenceActionButton = <
  T extends { id: number; sequence: MessageSequenceResponseDto },
>({
  row,
  deleteMessage,
  toggleMessage,
}: ActionButtonProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [isToggleLoading, setIsToggleLoading] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const handleCloseDeleteDialog = useCallback(() => setShowDelete(false), [])

  const redirect = useNavigate()
  const handleClickEdit = () => {
    redirect(`edit/${row.original.id}`)
  }
  const handleClickDelete = () => {
    setShowDelete(true)
  }

  const handleDeleteConfirmation = useCallback(async () => {
    if (!row.original?.id) {
      return
    }
    try {
      setIsDeleteLoading(true)
      await deleteMessage(row.original.id)
      setShowDelete(false)
    } catch (error) {
      /* empty */
    } finally {
      // Error state is handled in deleteMessage request
      setIsDeleteLoading(false)
    }
  }, [deleteMessage, row.original.id])

  const handleToggleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setIsToggleLoading(true)
      await toggleMessage(row.original.id, event.target.checked)
      setIsToggleLoading(false)
    } catch (error) {
      // Error state is handled in deleteMessage request
      setIsToggleLoading(false)
    }
  }

  return (
    <div className="relative flex gap-6">
      {isToggleLoading ? (
        <div className="flex min-w-[45px] items-center">
          <IconLoading className="mx-auto animate-spin" />
        </div>
      ) : (
        <Toggle
          name={String(row.original.id)}
          onChange={handleToggleChange}
          checked={
            row.original?.sequence?.status === MessageSequenceStatus.ACTIVE
          }
        />
      )}

      <DropdownMenu
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
      >
        <DropdownMenuTrigger className="cursor-pointer">
          <IconMore className="text-title hover:text-secondary-300 h-5 w-5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-40 p-1"
          forceMount
        >
          <DropdownMenuItem onClick={handleClickEdit}>
            <div className="flex items-center gap-2">
              <EditIcon />
              <span>Edit</span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleClickDelete}>
            <div className="flex items-center gap-2">
              <RemoveIcon />
              <span>Delete</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDeleteDialog
        show={showDelete}
        isDeleting={isDeleteLoading}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDeleteConfirmation}
      />
    </div>
  )
}
